import React, { useEffect, useMemo, useState } from "react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Copy, KeyRound, Plus, RotateCw, ShieldOff, ShieldCheck, Trash2, Settings2, History, ChevronDown } from "lucide-react";

const STATUS_COLORS = {
  active:   "bg-emerald-50 text-emerald-700 border-emerald-200",
  disabled: "bg-slate-100 text-slate-600 border-slate-300",
  revoked:  "bg-rose-50 text-rose-700 border-rose-200",
  expired:  "bg-amber-100 text-amber-800 border-amber-300",
};

const EVENT_COLORS = {
  created:            "bg-emerald-50 text-emerald-700 border-emerald-200",
  rotated:            "bg-amber-50 text-amber-800 border-amber-200",
  enabled:            "bg-emerald-50 text-emerald-700 border-emerald-200",
  disabled:           "bg-slate-100 text-slate-700 border-slate-200",
  revoked:            "bg-rose-50 text-rose-700 border-rose-200",
  scope_changed:      "bg-indigo-50 text-indigo-700 border-indigo-200",
  rate_limit_changed: "bg-sky-50 text-sky-700 border-sky-200",
  expiry_changed:     "bg-amber-50 text-amber-800 border-amber-200",
  updated:            "bg-slate-50 text-slate-600 border-slate-200",
};

const initialScopeMatrix = (modules, actions, seed) => {
  const m = {};
  modules.forEach((mod) => {
    m[mod] = {};
    actions.forEach((a) => { m[mod][a] = !!(seed?.[mod]?.includes(a)); });
  });
  return m;
};

function ScopeMatrix({ modules, actions, value, onChange, disabled }) {
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-md">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50">
          <tr>
            <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Module</th>
            {actions.map((a) => (
              <th key={a} className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold text-center">{a}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {modules.map((mod) => (
            <tr key={mod}>
              <td className="px-3 py-2 font-medium text-slate-800 capitalize">{mod}</td>
              {actions.map((a) => (
                <td key={a} className="px-3 py-2 text-center">
                  <Checkbox
                    checked={value[mod]?.[a] || false}
                    onCheckedChange={(v) => {
                      const next = { ...value, [mod]: { ...value[mod], [a]: !!v } };
                      onChange(next);
                    }}
                    disabled={disabled}
                    data-testid={`scope-${mod}-${a}`}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function matrixToScopes(m) {
  const out = {};
  Object.keys(m).forEach((mod) => {
    const actions = Object.keys(m[mod]).filter((a) => m[mod][a]);
    if (actions.length) out[mod] = actions;
  });
  return out;
}

function formatDate(iso, withTime = true) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return withTime ? d.toLocaleString() : d.toLocaleDateString();
}

function OneTimeSecretDialog({ tokenValue, onClose }) {
  const [copied, setCopied] = useState(false);
  const [ack, setAck] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(tokenValue);
      setCopied(true);
      toast.success("Token copied to clipboard");
    } catch {
      toast.error("Clipboard blocked — copy manually");
    }
  };

  return (
    <Dialog open onOpenChange={() => { /* prevent close until acknowledged */ }}>
      <DialogContent className="sm:max-w-[560px]" data-testid="secret-reveal-dialog"
                     onEscapeKeyDown={(e) => e.preventDefault()}
                     onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-600" /> Token secret (shown once)</DialogTitle>
          <DialogDescription>
            Copy this now. It will never be shown again and is stored only as a SHA-256 hash.
          </DialogDescription>
        </DialogHeader>

        <div className="p-3 rounded-md bg-slate-900 text-emerald-300 font-mono text-xs break-all border border-slate-700" data-testid="secret-plaintext">
          {tokenValue}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={copy} data-testid="btn-copy-secret" className="flex-1">
            <Copy className="w-4 h-4 mr-1.5" /> {copied ? "Copied ✓" : "Copy to clipboard"}
          </Button>
        </div>

        <label className="flex items-start gap-2 text-sm text-slate-700 mt-2">
          <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="secret-ack" />
          <span>I have saved this token in a secure location (n8n credentials, secrets manager, etc.).</span>
        </label>

        <DialogFooter>
          <Button onClick={onClose} disabled={!ack} data-testid="btn-close-secret"
                  className="bg-slate-900 hover:bg-slate-800 text-white">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TokenFormDialog({ open, onClose, initial, mode, catalogue, integrationUsers, onSubmit }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (!open) return;
    setForm({
      name: initial?.name || "",
      description: initial?.description || "",
      user_id: initial?.user_id || integrationUsers[0]?.id || "",
      rate_limit_rps: initial?.rate_limit_rps ?? catalogue?.default_rate_limit_rps ?? 20,
      rate_limit_rpm: initial?.rate_limit_rpm ?? catalogue?.default_rate_limit_rpm ?? 600,
      expires_at: initial?.expires_at ? initial.expires_at.slice(0, 10) : "",
      scopes_matrix: initialScopeMatrix(
        catalogue.modules, catalogue.actions,
        initial?.scopes || (mode === "create" ? catalogue.default_scopes_recommended : {})
      ),
    });
  }, [open, initial, mode, catalogue, integrationUsers]);

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    if (form.rate_limit_rps < 1 || form.rate_limit_rps > 1000) { toast.error("RPS must be 1–1000"); return; }
    if (form.rate_limit_rpm < 1 || form.rate_limit_rpm > 60000) { toast.error("RPM must be 1–60000"); return; }
    if (form.expires_at) {
      const d = new Date(form.expires_at);
      if (Number.isNaN(d.getTime())) { toast.error("Invalid expiry date"); return; }
    }
    const payload = {
      name: form.name.trim(),
      description: form.description || "",
      rate_limit_rps: parseInt(form.rate_limit_rps, 10),
      rate_limit_rpm: parseInt(form.rate_limit_rpm, 10),
      scopes: matrixToScopes(form.scopes_matrix),
      expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59Z").toISOString() : "",
    };
    if (mode === "create") {
      payload.user_id = form.user_id;
    }
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" data-testid="token-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {mode === "create" ? "Create API token" : "Edit token"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "The secret will be revealed once immediately after saving."
              : "Modify scopes, limits, expiry, or state. The secret is not changed."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Name *</Label>
              <Input required value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="token-name" className="mt-1.5" placeholder="e.g. n8n-production" />
            </div>
            <div>
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Integration user *</Label>
              {mode === "create" ? (
                <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="token-user"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {integrationUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input readOnly value={initial?.user_name || ""} className="mt-1.5 bg-slate-50" />
              )}
            </div>
          </div>

          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Description</Label>
            <Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })}
                      data-testid="token-description" className="mt-1.5"
                      placeholder="e.g. n8n inbound-email → CRM lead capture workflow" />
          </div>

          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider mb-2 block">Module scopes</Label>
            <ScopeMatrix
              modules={catalogue.modules}
              actions={catalogue.actions}
              value={form.scopes_matrix || {}}
              onChange={(v) => setForm({ ...form, scopes_matrix: v })}
            />
            <div className="text-[11px] text-slate-500 mt-1.5">
              Finance / banking / users / settings can never be scoped for tokens — they are hard-forbidden at the auth layer.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Rate limit — RPS <span className="text-slate-400 normal-case font-normal">(1–1000)</span></Label>
              <Input type="number" min={1} max={1000} value={form.rate_limit_rps}
                     onChange={(e) => setForm({ ...form, rate_limit_rps: e.target.value })}
                     data-testid="token-rps" className="mt-1.5 font-mono text-lg font-semibold" />
            </div>
            <div>
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Rate limit — RPM <span className="text-slate-400 normal-case font-normal">(1–60000)</span></Label>
              <Input type="number" min={1} max={60000} value={form.rate_limit_rpm}
                     onChange={(e) => setForm({ ...form, rate_limit_rpm: e.target.value })}
                     data-testid="token-rpm" className="mt-1.5 font-mono text-lg font-semibold" />
            </div>
            <div>
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Expiry (optional)</Label>
              <Input type="date" value={form.expires_at || ""}
                     onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                     data-testid="token-expiry" className="mt-1.5" />
              <div className="text-[11px] text-slate-400 mt-1">Leave blank for no expiry.</div>
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="token-form-cancel">Cancel</Button>
            <Button type="submit" data-testid="token-form-submit"
                    className="bg-amber-600 hover:bg-amber-700 text-white">
              {mode === "create" ? "Create token" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function TokenManagement({ integrationUsers = [] }) {
  const [tokens, setTokens] = useState([]);
  const [events, setEvents] = useState([]);
  const [catalogue, setCatalogue] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);            // null = create mode
  const [revealSecret, setRevealSecret] = useState(null);  // plaintext to show once
  const [confirm, setConfirm] = useState(null);            // {action:'revoke'|'rotate', token}
  const [expanded, setExpanded] = useState(null);          // id of row whose activity/audit is expanded

  const load = async () => {
    const [t, c, e] = await Promise.all([
      http.get("/auth/api-tokens"),
      http.get("/auth/api-token-scopes"),
      http.get("/auth/api-token-events?limit=100"),
    ]);
    setTokens(t.data); setCatalogue(c.data); setEvents(e.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const activeIntegrationUsers = useMemo(
    () => integrationUsers.filter((u) => u.role === "integration"),
    [integrationUsers]
  );

  const handleCreate = async (payload) => {
    try {
      const { data } = await http.post("/auth/api-tokens", payload);
      setRevealSecret(data.token);
      setOpenForm(false);
      toast.success("Token created");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const handleUpdate = async (payload) => {
    try {
      await http.patch(`/auth/api-tokens/${editing.id}`, payload);
      setOpenForm(false); setEditing(null);
      toast.success("Token updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const doRotate = async (token) => {
    try {
      const { data } = await http.post(`/auth/api-tokens/${token.id}/rotate`);
      setRevealSecret(data.token);
      toast.success("Token rotated — previous secret is now invalid");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setConfirm(null);
  };
  const doRevoke = async (token) => {
    try {
      await http.delete(`/auth/api-tokens/${token.id}`);
      toast.success("Token revoked");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setConfirm(null);
  };
  const doToggleActive = async (token) => {
    try {
      await http.patch(`/auth/api-tokens/${token.id}`, { active: !token.active });
      toast.success(token.active ? "Token disabled" : "Token enabled");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!catalogue) return <div className="text-slate-500 text-sm p-4">Loading…</div>;

  return (
    <div className="space-y-4">
      {/* Header actions */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">API tokens</h3>
          <p className="text-xs text-slate-500">Issue, rotate, and audit access for n8n and external agents. Admin-only.</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpenForm(true); }}
                data-testid="btn-new-token"
                disabled={activeIntegrationUsers.length === 0}
                className="bg-amber-600 hover:bg-amber-700 text-white">
          <Plus className="w-4 h-4 mr-1.5" /> New token
        </Button>
      </div>

      {activeIntegrationUsers.length === 0 && (
        <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="no-integration-user-banner">
          No user with role <span className="font-mono">integration</span> exists. Create one under the <strong>Users</strong> tab first — tokens can only be issued to integration users.
        </div>
      )}

      {/* Tokens table */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-3 font-semibold">Token</th>
                <th className="text-left px-4 py-3 font-semibold">User</th>
                <th className="text-left px-4 py-3 font-semibold">Scopes</th>
                <th className="text-center px-4 py-3 font-semibold">Limits</th>
                <th className="text-left px-4 py-3 font-semibold">Created / Expiry</th>
                <th className="text-left px-4 py-3 font-semibold">Last used</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tokens.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-500" data-testid="tokens-empty">No tokens yet — create one to get started.</td></tr>
              )}
              {tokens.map((t) => {
                const statusCls = STATUS_COLORS[t.status] || STATUS_COLORS.disabled;
                const scopeSummary = Object.keys(t.scopes || {}).length
                  ? Object.entries(t.scopes).map(([mod, acts]) => `${mod}:${acts.join(",")}`).join(" · ")
                  : "no scopes";
                return (
                  <React.Fragment key={t.id}>
                    <tr className="align-top" data-testid={`token-row-${t.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{t.name}</div>
                        <div className="text-[11px] text-slate-500 font-mono">…{t.last_four}</div>
                        {t.description && <div className="text-xs text-slate-500 mt-1 max-w-xs">{t.description}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <div>{t.user_name}</div>
                        <div className="text-[11px] text-slate-400">{t.tenant_id}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 max-w-xs">
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(t.scopes || {}).map(([mod, acts]) => (
                            <span key={mod} className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-[10px]">
                              <span className="font-semibold text-slate-800 mr-1 capitalize">{mod}</span>
                              <span className="text-slate-500">{acts.join(", ")}</span>
                            </span>
                          ))}
                          {!Object.keys(t.scopes || {}).length && <span className="text-rose-500 italic">no scopes</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center whitespace-nowrap">
                        <div className="text-slate-900 font-mono font-semibold">{t.rate_limit_rps}<span className="text-slate-400 font-normal">/s</span></div>
                        <div className="text-slate-500 font-mono text-[11px]">{t.rate_limit_rpm}/min</div>
                      </td>
                      <td className="px-4 py-3 text-xs whitespace-nowrap">
                        <div className="text-slate-700">{formatDate(t.created_at, false)}</div>
                        <div className="text-slate-400">by {t.created_by_name || t.created_by || "—"}</div>
                        <div className="mt-1 text-slate-700">exp: {t.expires_at ? formatDate(t.expires_at, false) : "never"}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{formatDate(t.last_used_at)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border capitalize ${statusCls}`}
                              data-testid={`token-status-${t.id}`}>
                          {t.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => { setEditing(t); setOpenForm(true); }}
                                  data-testid={`btn-edit-token-${t.id}`} title="Edit scopes / limits / expiry">
                            <Settings2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setConfirm({ action: "rotate", token: t })}
                                  data-testid={`btn-rotate-token-${t.id}`} title="Rotate secret"
                                  disabled={!t.active || t.status === "expired"}>
                            <RotateCw className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => doToggleActive(t)}
                                  data-testid={`btn-toggle-token-${t.id}`} title={t.active ? "Disable" : "Enable"}
                                  disabled={t.status === "revoked" || t.status === "expired"}>
                            {t.active ? <ShieldOff className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setConfirm({ action: "revoke", token: t })}
                                  data-testid={`btn-revoke-token-${t.id}`} title="Revoke permanently"
                                  className="text-rose-700 hover:bg-rose-50 border-rose-200"
                                  disabled={t.status === "revoked"}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="outline"
                                  onClick={() => setExpanded(expanded === t.id ? null : t.id)}
                                  data-testid={`btn-expand-token-${t.id}`} title="Show activity">
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded === t.id ? "rotate-180" : ""}`} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {expanded === t.id && (
                      <tr>
                        <td colSpan={8} className="bg-slate-50/50 px-6 py-4 border-t border-slate-100">
                          <TokenActivityInline tokenId={t.id} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Global audit log */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <History className="w-4 h-4 text-slate-500" />
          <div className="font-heading font-semibold text-slate-900">Admin audit — token events</div>
          <span className="text-xs text-slate-500 ml-auto">{events.length} recent events</span>
        </div>
        {events.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-8" data-testid="token-events-empty">No token events yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50">
                <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="text-left px-4 py-2 font-semibold">When</th>
                  <th className="text-left px-4 py-2 font-semibold">Event</th>
                  <th className="text-left px-4 py-2 font-semibold">Token</th>
                  <th className="text-left px-4 py-2 font-semibold">Admin</th>
                  <th className="text-left px-4 py-2 font-semibold">Changes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {events.map((e) => (
                  <tr key={e.id} className="text-sm" data-testid={`event-row-${e.id}`}>
                    <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">{formatDate(e.timestamp)}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${EVENT_COLORS[e.event_type] || EVENT_COLORS.updated}`}>
                        {e.event_type.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-slate-700">{e.token_name}</td>
                    <td className="px-4 py-2 text-slate-700">{e.admin_name}</td>
                    <td className="px-4 py-2 text-xs text-slate-600 font-mono max-w-md truncate">
                      {Object.keys(e.changes || {}).length
                        ? Object.entries(e.changes).map(([k, v]) => `${k}=${JSON.stringify(v).slice(0, 80)}`).join(" · ")
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit form */}
      {openForm && (
        <TokenFormDialog
          open
          onClose={() => { setOpenForm(false); setEditing(null); }}
          initial={editing}
          mode={editing ? "edit" : "create"}
          catalogue={catalogue}
          integrationUsers={activeIntegrationUsers}
          onSubmit={editing ? handleUpdate : handleCreate}
        />
      )}

      {/* Reveal secret */}
      {revealSecret && <OneTimeSecretDialog tokenValue={revealSecret} onClose={() => setRevealSecret(null)} />}

      {/* Confirm rotate / revoke */}
      {confirm && (
        <AlertDialog open onOpenChange={(o) => !o && setConfirm(null)}>
          <AlertDialogContent data-testid="confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading">
                {confirm.action === "rotate" ? "Rotate this token?" : "Revoke this token?"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {confirm.action === "rotate"
                  ? <>The current secret ending in <span className="font-mono">…{confirm.token.last_four}</span> will be invalidated immediately. Any active workflow using it will start failing until you update its credential with the new secret.</>
                  : <>Revoking is permanent. Any workflow using <span className="font-mono">…{confirm.token.last_four}</span> will start returning 401. This cannot be undone — you'll need to create a fresh token.</>}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirm-proceed"
                onClick={() => confirm.action === "rotate" ? doRotate(confirm.token) : doRevoke(confirm.token)}
                className={confirm.action === "revoke" ? "bg-rose-600 hover:bg-rose-700" : "bg-amber-600 hover:bg-amber-700"}>
                {confirm.action === "rotate" ? "Rotate now" : "Revoke permanently"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

function TokenActivityInline({ tokenId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    http.get("/auth/api-activity?limit=200").then((r) => {
      setRows(r.data.filter((x) => x.token_id === tokenId).slice(0, 15));
    });
  }, [tokenId]);
  if (!rows) return <div className="text-xs text-slate-400">Loading activity…</div>;
  if (rows.length === 0) return <div className="text-xs text-slate-500">No API activity recorded for this token yet.</div>;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Recent API calls (last 15)</div>
      <table className="min-w-full text-xs">
        <thead className="bg-white">
          <tr className="text-[10px] uppercase tracking-wider text-slate-400">
            <th className="text-left px-2 py-1">When</th>
            <th className="text-left px-2 py-1">Method</th>
            <th className="text-left px-2 py-1">Path</th>
            <th className="text-center px-2 py-1">Status</th>
            <th className="text-left px-2 py-1">Detail</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100">
              <td className="px-2 py-1 text-slate-500">{formatDate(r.timestamp)}</td>
              <td className="px-2 py-1 font-mono text-slate-700">{r.method}</td>
              <td className="px-2 py-1 font-mono text-slate-700">{r.path}</td>
              <td className="px-2 py-1 text-center">
                <span className={`inline-block px-1.5 rounded font-mono font-semibold ${
                  r.status_code < 400 ? "text-emerald-700 bg-emerald-50" :
                  r.status_code === 429 ? "text-amber-700 bg-amber-50" :
                  "text-rose-700 bg-rose-50"
                }`}>{r.status_code}</span>
              </td>
              <td className="px-2 py-1 text-slate-600 truncate max-w-md">{r.error || r.action}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
